import { decorateReward } from '@/common/reward/linkable';
import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { validateDateAfter, validateFormat } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { LiveopsItemModel, LiveopsRewardType, LiveopsType } from '@/modules/liveops/item/types';
import { ROUTE_NAME_LIVEOPS } from '@/modules/liveops/router';
import { useLiveopsReward } from '@/modules/liveops/shared/reward';
import { liveopsPointService } from '@/services/liveops';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

export function useLiveops() {
    const route = useRoute();
    const router = useAppRouter();

    const { fetchLinkableRewards, linkableRewards } = useLiveopsReward();

    const model = reactive<LiveopsItemModel>({
        id: StringUtils.toNumber(route.params.id),
        name: null,
        type: null,
        rewardType: null,
        status: null,
        betCosts: null,
        startTime: new Date(),
        closeTime: null,
        endTime: null,
        image: null,
        imageFile: null,
        imageStatus: null,
        icon: null,
        iconFile: null,
        iconStatus: null,

        milestones: [],
        leaderboards: [],

        canEdit: false,
        canEditContent: false,
        canEditMilestone: false,
        canEditLeaderboard: false,
        canEnable: false,
        canDisable: false,
        canDelete: false,

        get isBetCostsShown() {
            return this.type === LiveopsType.Normal;
        }
    });

    const validations = {
        name: { required },
        type: { required },
        rewardType: { required },
        betCosts: {
            requiredIf: requiredIf(() => model.isBetCostsShown),
            validateFormat: validateFormat(/^\d+(,\d+)*$/)
        },
        startTime: { required, validateDateAfter: validateDateAfter() },
        closeTime: { required, validateDateAfter: validateDateAfter('startTime') },
        endTime: { required, validateDateAfter: validateDateAfter('closeTime') }
    };

    const v$ = useVuelidate(validations, model);

    const setDataWhenRewardTypeChanges = (value: LiveopsRewardType) => {
        if (value == LiveopsRewardType.Leaderboard) {
            model.type = LiveopsType.Normal;
        }
    };

    watch(() => model.rewardType, setDataWhenRewardTypeChanges);

    const fetch = useCrudActions().executeAction({
        canExecute: () => model.id != null,
        request: () => liveopsPointService.item.get(model.id),
        onSuccess: ({ data }) => {
            data.startTime = new Date(data.startTime);
            data.closeTime = new Date(data.closeTime);
            data.endTime = new Date(data.endTime);
            Object.assign(model, data);
        },
        onError: () => router.push({ name: ROUTE_NAME_LIVEOPS.ITEM.INDEX(model.rewardType) })
    });

    const decorateRewards = async () => {
        if (!model.id) return;

        if (linkableRewards.value.length === 0) {
            await fetchLinkableRewards();
        }

        model.milestones = model.milestones.map(reward => decorateReward(reward, linkableRewards.value));
        model.leaderboards = model.leaderboards.map(reward => decorateReward(reward, linkableRewards.value));
    };

    return { model, v$, fetch, decorateRewards };
}
